/**
 * 支付轮询的判定逻辑（纯函数）。
 *
 * 为什么把「判断」和「发请求」分开：
 *   付款这一步最怕两件事 ——
 *     a. 顾客明明付了，界面却一直转圈（体验灾难，顾客会喊人）；
 *     b. 顾客没付，界面却说付好了（钱的灾难，绝不能发生）。
 *   这两件事都是「判断」的问题，所以判断写成纯函数、逐条测，
 *   定时器和网络请求只是把它的结论执行出来。
 *
 * 铁律：**只有服务端明确说 paid === true 才算付款成功。**
 *      请求失败、超时、订单状态看不懂，一律按「还没付」处理并继续等。
 */

import type { PayStatus } from '../api/types';

export type PayPhase =
  /** 还在等顾客付款 */
  | 'waiting'
  /** 已确认收到钱 */
  | 'paid'
  /** 二维码过期了，要换一张 */
  | 'expired'
  /** 订单被取消了 */
  | 'cancelled';

export interface PayDecision {
  phase: PayPhase;
  /** 给顾客看的一句话 */
  message: string;
}

/**
 * 根据一次轮询结果决定下一步。
 *
 * @param status 服务端返回的支付状态；请求失败传 null
 * @param now 当前时间（毫秒）；显式传入，测试才能稳定
 * @param expiresAt 二维码过期时间（毫秒）
 */
export function decidePayPhase(
  status: PayStatus | null,
  now: number,
  expiresAt: number,
): PayDecision {
  if (status?.paid === true) {
    return {
      phase: 'paid',
      message: status.isTestMode ? '已确认（测试收款，不计入营业额）' : '收款成功',
    };
  }

  if (status?.status === 'CANCELLED') {
    return { phase: 'cancelled', message: '这一单已取消，请叫服务员' };
  }

  // 请求失败（null）时**不**判过期：网络抖一下就让顾客重新扫码，纯属添乱。
  // 只有拿得到服务端回应、且确实过了有效期，才换码。
  if (status !== null && now >= expiresAt) {
    return { phase: 'expired', message: '二维码超时了，正在换一张' };
  }

  return { phase: 'waiting', message: '请用微信扫码付款' };
}

/** 二维码剩余秒数（不小于 0） */
export function remainingSeconds(expiresAt: number, now: number): number {
  return Math.max(0, Math.ceil((expiresAt - now) / 1000));
}

/**
 * 轮询间隔的统一出口。
 *
 * 服务端会告诉你建议间隔（pollIntervalMs），但前端不能无条件信任它 ——
 * 万一配置成 0 或 10 毫秒，触屏机会把后端打爆。
 * 所以这里夹在 1~5 秒之间。
 */
export function safePollIntervalMs(suggested: number | undefined): number {
  const fallback = 2000;
  if (suggested === undefined || !Number.isFinite(suggested)) return fallback;
  return Math.min(5000, Math.max(1000, Math.floor(suggested)));
}
